'use client';

/**
 * Fires a productClick event for Google Analytics. *Note: Do not ever push PII into the data layer!!*
 *
 * @example
 * const { trackEvent } = useProductClickEvent();
 * trackEvent({
        dealerId: dealerCode,
        products: [productData],
        list: ORDER_STATUS_ITEM_LINK_GA_LIST_NAME,
        model: equipmentModel
      );
 */

import { useCallback } from 'react';
import { sendGTMEvent } from '@next/third-parties/google';
import {
  DEFAULT_BUSINESS_UNIT,
  EVENT_SELECT_ITEM
} from 'constants/analyticsConstants';
import { getValueOrElseEmptyString } from 'utils/string';
import { useTranslations } from 'next-intl';
import { getInventoryStatus } from 'utils/analytics';
interface ProductClickEventProps {
  cartId?: string;
  currency?: string;
  dealerId?: string | number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  products: any[];
  list: string;
  model?: string;
  paymentMethods?: string[];
  storeName: string;
}

const DEFAULT_BRAND = 'Cat';
const CHECKFITMENT = 'fitn';

export const useProductClickEvent = () => {
  const t = useTranslations();
  const CONTACT_YOUR_DEALER = t('CONTACT_YOUR_DEALER');

  const buildEvent = useCallback(
    ({
      cartId,
      currency,
      dealerId,
      products,
      list,
      model,
      paymentMethods,
      storeName
    }: ProductClickEventProps) => {
      const cartDetails = {
        cartId: getValueOrElseEmptyString(cartId)
      };

      const items = products.map((item, index) => {
        const productName = item.name ?? item.partName;

        return {
          item_id: getValueOrElseEmptyString(item.partNumber),
          item_name: getValueOrElseEmptyString(productName ?? item.description),
          affiliation: DEFAULT_BUSINESS_UNIT,
          coupon: undefined,
          discount: undefined,
          index: index + 1,
          item_brand: DEFAULT_BRAND,
          item_category: getValueOrElseEmptyString(item.category),
          item_category2: undefined,
          item_category3: undefined,
          item_category4: undefined,
          item_category5: undefined,
          item_variant: undefined,
          location_id: getValueOrElseEmptyString(storeName),
          price: getValueOrElseEmptyString(
            item?.unformattedUnitPrice ||
              item?.priceWithoutGST?.formatted?.unitPrice
          ),
          quantity: getValueOrElseEmptyString(item.quantity),
          p_inventory_status: getValueOrElseEmptyString(
            getInventoryStatus(
              CONTACT_YOUR_DEALER,
              item.availability,
              item.sourcingDetails
            )
          ),
          p_weight: getValueOrElseEmptyString(item.weight),
          p_serial_number: undefined,
          p_kit_id: getValueOrElseEmptyString(item.kitId),
          p_check_fitment: CHECKFITMENT,
          p_attributes_visible: getValueOrElseEmptyString(
            item.productAttributesVisible
          )
        };
      });

      return {
        event: EVENT_SELECT_ITEM,
        cartDetails,
        ecommerce: {
          item_list_id: getValueOrElseEmptyString(list),
          item_list_name: getValueOrElseEmptyString(model),
          items
        }
      };
    },
    [CONTACT_YOUR_DEALER]
  );

  const trackEvent = useCallback(
    (data: ProductClickEventProps) => sendGTMEvent(buildEvent(data)),
    [buildEvent]
  );

  return { trackEvent };
};
